'use client';

import { Box, Button, TextField } from '@mui/material';
import dayjs, { Dayjs } from "dayjs"; // For date formatting
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from "yup";
import axios from 'axios';

import { PhoneNumberUtil } from "google-libphonenumber";
import { useRouter } from "next/router";
import SnackbarAlert from "@/shared/alert";

const phoneUtil = PhoneNumberUtil.getInstance();


export default function Form() {

    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

    const [open, setOpen] = useState(false); // Controls DatePicker open state

    const [isSubmitForm, setSubmitForm] = useState(false);

    const [showSnackbar, setShowSnackbar] = useState(false);

    const router = useRouter();


    const formik = useFormik({
        initialValues: {
            name: "",
            location: "",
            email: "",
            phone: "",
            eventType: "",
            guests: "",
            eventDate: "",
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Name is required"),
            location: Yup.string().required("Location is required"),
            email: Yup.string().email("Invalid email").required("Email is required"),
            phone: Yup.string()
                .required("Phone number is required")
                .test("is-valid-phone", "Invalid phone number", (value) => {
                    if (!value) return false;
                    try {
                        const parsedNumber = phoneUtil.parseAndKeepRawInput(value, "US");
                        return phoneUtil.isValidNumber(parsedNumber);
                    } catch (error) {
                        console.log(error, "This is error ...");
                        return false;
                    }
                }),
            eventType: Yup.string().required("Event type is required"),
            guests: Yup.number()
                .positive("Must be a positive number")
                .integer("Must be a whole number")
                .required("Number of guests is required"),
            eventDate: Yup.date().required("Event date is required"),
        }),
        onSubmit: async (values, form) => {
            console.log("Form Data:", values);

            try {


                setSubmitForm(true);

                const data = {
                    name: {
                        firstName: values.name
                    },
                    contact: {
                        email: values.email,
                        phone: {
                            dialCode: 0,
                            iso2: "string",
                            country: "string",
                            number: values.phone
                        },
                    },
                    message: "",
                    subFields: {
                        address: values.location,
                        eventDate: values.eventDate,
                    }
                }

                await axios.post(
                    "https://api.tablacuisine.com/api/website/query", { ...data, type: 'CATERING_CONTACT_US' }
                );

                const goatResponse = await axios.post(
                    "https://api.goatcampaign.com/v1/access-token",
                    {
                        tenantId: "63cd8baecbab01ef459c147c",
                        secretKey: "89e25562-49ff-43e6-960e-e41fab71b8ba"
                    }
                );

                const token = goatResponse.data?.['data']?.['tenantToken'];


                const response = await axios.post(
                    "https://api.goatcampaign.com/v1/mail/send",
                    {
                        from: "info@tablacuisine.com",
                        to: "info@tablacuisine.com",
                        subject: "Tabla Cuisine - Catering Form",
                        html: `
            </br></br>
            <p>Name: ${values?.name}</p></br>
            <p>Email ID: ${values?.email}</p></br>
            <p>Phone Number: ${values?.phone}</p></br>
            <p>Event Date: ${values?.eventDate}</p></br>
            <p>Event Location: ${values?.location}</p></br>
            <p>Type Of Event: ${values?.eventType}</p></br>
            <p>Number Of Guests: ${values?.guests}</p></br>
          `,
                    },
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`,
                            "Content-Type": "application/json",
                        }
                    }
                );

                await axios.post(
                    "https://api.goatcampaign.com/v1/mail/send",
                    {
                        from: "info@tablacuisine.com",
                        to: "events@tablacuisine.com",
                        subject: "Tabla Cuisine - Catering Form",
                        html: `
            </br></br>
            <p>Name: ${values?.name}</p></br>
            <p>Email ID: ${values?.email}</p></br>
            <p>Phone Number: ${values?.phone}</p></br>
            <p>Event Date: ${values?.eventDate}</p></br>
            <p>Event Location: ${values?.location}</p></br>
            <p>Type Of Event: ${values?.eventType}</p></br>
            <p>Number Of Guests: ${values?.guests}</p></br>
          `,
                    },
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`,
                            "Content-Type": "application/json",
                        }
                    }
                );


                if (response) {
                    setShowSnackbar(true);
                    form.resetForm();
                }

                setSubmitForm(false);

                router.push("/thankyou");

            } catch (error) {
                console.log(error, "This is error");
                setSubmitForm(false);
            }
        },
    });

    return (<LocalizationProvider dateAdapter={AdapterDayjs}>

        <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 450, mx: "auto" }}
        >
            <h2 className="text-2xl font-bold text-red-900 text-center uppercase tracking-[.08em]">
                Request A Quote
            </h2>
            <div className="w-16 h-1 bg-red-900 mx-auto mt-0 mb-3"></div>
            <div className="grid grid-cols-2 gap-5">
                <TextField
                    label="Name"
                    variant="outlined"
                    fullWidth
                    {...formik.getFieldProps("name")}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                />

                <TextField
                    label="Location of Event"
                    variant="outlined"
                    fullWidth
                    {...formik.getFieldProps("location")}
                    error={formik.touched.location && Boolean(formik.errors.location)}
                    helperText={formik.touched.location && formik.errors.location}
                />

                <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    {...formik.getFieldProps("email")}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                />

                <TextField
                    label="Phone Number"
                    variant="outlined"
                    fullWidth
                    {...formik.getFieldProps("phone")}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                />

                <TextField
                    label="Type of Event"
                    variant="outlined"
                    fullWidth
                    {...formik.getFieldProps("eventType")}
                    error={formik.touched.eventType && Boolean(formik.errors.eventType)}
                    helperText={formik.touched.eventType && formik.errors.eventType}
                />

                <TextField
                    label="Number of Guests"
                    variant="outlined"
                    type="number"
                    fullWidth
                    {...formik.getFieldProps("guests")}
                    error={formik.touched.guests && Boolean(formik.errors.guests)}
                    helperText={formik.touched.guests && formik.errors.guests}
                />
            </div>
            <DatePicker
                label="Event Date"
                open={open} // Controls visibility
                onOpen={() => setOpen(true)} // Open when input is clicked
                onClose={() => setOpen(false)} // Close when date is selected
                value={selectedDate}
                onChange={(newDate: Dayjs | null) => {
                    setSelectedDate(newDate);
                    formik.setFieldValue("eventDate", dayjs(newDate).format("YYYY-MM-DD"));
                    setOpen(false); // Close calendar after selecting a date
                }}
                slotProps={{
                    textField: {
                        fullWidth: true,
                        onClick: () => setOpen(true), // Open picker when clicking input field
                        error: formik.touched.eventDate && Boolean(formik.errors.eventDate),
                        helperText: formik.touched.eventDate && formik.errors.eventDate,
                    },
                }}
            />


            <Button disabled={isSubmitForm} type="submit" className="button-green mt-2" variant="contained">
                Submit
            </Button>
        </Box>

        {showSnackbar && <SnackbarAlert message="Query Sent Successfully!" />}

    </LocalizationProvider>
    )
}